<template>
  <nav aria-label="Search navigation" class="relative flex flex-col md:flex-row md:items-center justify-between py-[2rem] px-[1.5rem] md:py-[3rem] md:px-0 md:pr-[2.142857rem]">
    <div class="flex items-center group cursor-pointer pb-[0.75rem] md:p-0" v-if="canGoBack">
      <span  
        aria-label="Go back to the previous page" 
        title="Go back to the previous page" 
        class="flex md:p-0 text-[1.2857rem] font-medium h-auto leading-[1.4464rem] text-ash-500 group-hover:text-gold-100" 
        :id="id"
        @keydown.space.prevent="goBack" 
        @click.prevent="goBack" @keydown.enter.prevent="goBack" 
        role="link"
        tabindex="0"
      >
        <div class="h-[1.446428rem]">
          <Icon :icon="'angle-left'" class="mr-[0.714285rem] w-[0.642857rem] !h-[1.285714rem] group-hover:text-gold-100" />
        </div>
      Back
    </span>
    </div>
    <div v-else :class="pageTitle ? 'w-full lg:w-3/4 p-3' : ''">
      <div v-if="pageTitle" class="flex items-center gap-2">
        <div
          class="flex items-center bg-gray-200 text-gray-900 px-[1rem] py-[0.5rem] text-[1rem] rounded w-full">
          <div class="flex items-center flex-grow font-normal">
            <Icon icon="hashtag"  class="text-pink-600 mr-[1.43rem]" />
            <span class="mr-2">{{ pageTitle }}</span>
          </div>
          <Icon icon="x" packPrefix="far" class="text-gray-900 cursor-pointer hover:opacity-70"
            @click="clearFilter" />
        </div>
      </div>
      <Heading v-else :level="2" class="pb-[0.75rem] md:p-0" color="text-ash-200">
        {{ activeTitle }}
      </Heading>
    </div>
    <div class="px-0 w-full md:px-[0.571428rem] md:w-1/4 pt-[0.75rem] md:pt-0" v-if="searchBar">
      <SearchBar placeholder="Search Take 5 Daily..."
        :id="`${layoutId}-search`"
        class="h-[2.857142rem] pt-[0.357142rem] pr-[2.5rem] pb-[0.428571rem] pl-[2.75rem] rounded-[0.428571rem!important] searchbar"
        customIconClass="absolute left-[1rem] top-[0.857142rem]" customInputClass="w-full appearance-none text-[1.142857rem] md:text-[1rem]"
        :blogPage="true" v-if="searchBar" :searchValue="searchValue">
      </SearchBar>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref} from "vue"
import SearchBar from "../../molecule/search/searchBar.vue"
import Heading from "../../atom/heading.vue"
import Icon from "../../atom/icon.vue"
import { useRoute, useRouter } from "vue-router"
import { URL_CONFIG } from "~~/shared/utils/url.const"

const props = defineProps({
  searchBar: {
    type: Boolean,
    default: true
  },
  fallbackUrl: {
    type: String,
    default: ''
  },
  layoutId: {
    type: String,
    default: ''
  }
})

const route = useRoute();
const router = useRouter();

const canGoBack = computed(() => {
  return (
    route.path.includes("/article/") ||
    route.path.includes("/video/") ||
    route.path.includes("/podcast/") ||
    route.path.includes("/press-releases/") ||
    route.path.startsWith(`${URL_CONFIG.FAQ}/`)
  );
});

const searchValue = ref<string>('')

const paramValue = route.query.search as string | undefined
if (paramValue) { 
  searchValue.value = paramValue;
}

const activeTitle = computed(() => {
  let title = "Most Recent";
  if (route.path.endsWith("articles")) {
    title = "Articles";
  } else if (route.path.endsWith("videos")) {
    title = "Videos";
  } else if (route.path.endsWith("podcasts")) {
    title = "Podcasts";
  } else if (route.path.endsWith("press")) {
    title = "As Seen In";
  }
  return title;
});

const goBack = () => {
  if (typeof window !== 'undefined' && window.history?.length > 2) {
    router.back();
  } else {
    router.push(props.fallbackUrl);
  }
}

const pageTitle = computed(() => {
  const t = route.query.t;
  return Array.isArray(t) ? t[0] : t || "";
});

const id = computed(() => props.layoutId || 'back-nav')

const clearFilter = () => {
  router.push({ path: route.path, query: {} });
}
</script>
